import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { onServerPrefetch, onMounted, onBeforeUnmount, ref, computed, hydrateOnIdle, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import { useCategoriesStore } from '@/store/categories-store'
import Layout from '@/layouts/ModernTwoColumnsLayout.vue'
import TheEnd from '@/components_new/TheEnd/TheEnd.vue'
import GameCardCarousel from '@/components_new/GameCardCarousel.vue'
import PromoCategories from '@/components_new/PromoCategories/PromoCategories.vue'
import { useLayoutStore } from '@/store/layout-store'
import ModalReportBug from '@/components_new/ModalItems/components/ModalReportBug.vue'
import LazyHydration from '@/components_new/LazyHydration.vue'
import HeadMetaGame from '@/pages/Games/components/HeadMetaGame.vue'
import Breadcrumbs from '@/components_new/Breadcrumbs.vue'
import { useGamePageStore } from './game-store'
import GameContainer from './components/GameContainer.vue'
import GameDetails from './components/GameDetails.vue'
import { ROUTE_NAMES } from '@/router/constants'
import { useSetPageData } from '@/utils/vue-hooks/use-set-page-data'
import { useTr } from '@/composables/use-tr'
import { useAppStore } from '@/store/app-store'
import { useUrl } from '@/composables/url-helpers'
import { useLoggerStore } from '@/store/logger'
import { useI18nAddon } from '@/i18n/i18n'

type RecommendationTitle = 'related' | 'top_playgama' | 'recommended' | 'random'

export default /*@__PURE__*/_defineComponent({
  __name: 'Game',
  setup(__props) {

const categoriesStore = useCategoriesStore()
const gamePageStore = useGamePageStore()
const loggerStore = useLoggerStore()
const route = useRoute()
const router = useRouter()
const tr = useTr()
const appStore = useAppStore()
const { currentLocale } = useI18nAddon()

const { game, recommendations, gameCatInfo } = storeToRefs(gamePageStore)
const layoutStore = useLayoutStore()
const { setIsReportBugModalOpen } = layoutStore

const isGameStarted = ref(false)
const hasGameSeries = computed(() => game.value?.series?.length)
const seriesGames = computed(() => recommendations.value?.series)

const firstRecommendationsGameBlock = computed(() => {
    if (hasGameSeries.value && seriesGames.value?.length) {
        const seriesTitle = gameCatInfo.value?.categoriesInfo?.find((cat) => cat.categoryType === 'series')?.title || ''
        return {
            title: seriesTitle,
            games: seriesGames.value,
            groupType: 'series',
            rows: 1,
        }
    }
    if (showRelatedGames.value) {
        return {
            title: showRecommendationsTitle('related'),
            games: relatedGames.value,
            groupType: 'related',
        }
    }
    return null
})

const breadcrumps = computed(() => {
    const categoryPath = gameCatInfo.value?.pathCategories.map((cat) => ({ title: cat.title, href: cat.href })) ?? []

    return game?.value
        ? [
              {
                  title: tr('game.main_title'),
                  href: { name: ROUTE_NAMES.MAIN },
              },
              ...categoryPath,
              {
                  title: game.value?.title,
                  href: { name: ROUTE_NAMES.GAME_PAGE, params: { game: game.value?.hru } },
                  disabled: true,
              },
          ]
        : []
})

const isReportModalOpen = computed(() => layoutStore.isReportBugModalOpen)
const relatedGames = computed(() => recommendations.value?.related)
const showRelatedGames = computed(() => (relatedGames.value ? relatedGames.value.length > 0 : true))
const spotlightGames = computed(() => recommendations.value?.top_playgama)
const showSpotlightGames = computed(() => (spotlightGames.value ? spotlightGames.value.length > 0 : true))
const recommendedGames = computed(() => recommendations.value?.recommended)
const showRecommendedGames = computed(() => (recommendedGames.value ? recommendedGames.value.length > 0 : true))
const randomGames = computed(() => recommendations.value?.random)
const showRandomGames = computed(() => (randomGames.value ? randomGames.value.length > 0 : true))

function showRecommendationsTitle(title: RecommendationTitle): string {
    switch (title) {
        case 'related':
            return tr('game.related')
        case 'top_playgama':
            return tr('game.top_playgama')
        case 'recommended':
            return tr('game.recommended')
        case 'random':
            return tr('game.random')
    }
}

function startGame() {
    isGameStarted.value = true
}

async function updateData(routeHRU: string) {
    // Prevents the request from being made twice if the data is already in the store
    if (!gamePageStore.isDataValid(routeHRU)) {
        isGameStarted.value = false
        gamePageStore.resetGameData()
        await gamePageStore.setGameWithRecommendations(routeHRU)
    }
}

const { getCategoryRoute } = useUrl()

function processRedirectResponse() {
    const { gameRedirect } = gamePageStore
    if (!gameRedirect) {
        return
    }
    const { redirectTarget, status: statusCode } = gameRedirect

    let redirectURL = ''

    if (redirectTarget.type === 'game') {
        redirectURL = router.resolve({
            name: ROUTE_NAMES.GAME_PAGE,
            params: { game: redirectTarget.game_hru },
        }).href
    } else if (redirectTarget.type === 'category') {
        redirectURL = router.resolve(
            getCategoryRoute({ hru: redirectTarget.cat_hru, type: redirectTarget.cat_type }),
        ).href
    } else if (redirectTarget.type === 'main-page') {
        redirectURL = router.resolve({
            name: ROUTE_NAMES.MAIN,
        }).href
    }

    appStore.setPageStatus(statusCode)
    appStore.setTargetURL(redirectURL)
}

onServerPrefetch(async () => {
    await Promise.all([updateData(route.params.game as string), categoriesStore.setMainData()])
    processRedirectResponse()
})

useSetPageData(() => game.value && { content_category: game.value.categories, provider: game.value.source || '' })

onMounted(async () => {
    await Promise.all([updateData(route.params.game as string), categoriesStore.setMainData()])
    processRedirectResponse()
    if (game.value) {
        loggerStore.logEvent({
            event: 'custom_event',
            eventName: 'game_preview',
            gameCategories: game.value.categories,
        })
    }
})

onBeforeUnmount(() => {
    gamePageStore.resetGameData()
})

watch(currentLocale, async () => {
    await Promise.all([updateData(route.params.game as string), categoriesStore.setMainData()])
    processRedirectResponse()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(HeadMetaGame),
    _createVNode(Layout, null, {
      default: _withCtx(() => [
        _createElementVNode("main", {
          class: _normalizeClass(_ctx.$style.page)
        }, [
          _createElementVNode("div", null, [
            _createVNode(GameContainer, {
              game: _unref(game),
              categories: _unref(gameCatInfo),
              isGameStarted: isGameStarted.value,
              onStartPlayingGame: startGame,
              onOpenReportModal: _cache[0] || (_cache[0] = ($event: any) => (_unref(setIsReportBugModalOpen)(true)))
            }, null, 8, ["game", "categories", "isGameStarted"])
          ]),
          (firstRecommendationsGameBlock.value)
            ? (_openBlock(), _createBlock(GameCardCarousel, {
                key: 0,
                "data-interface-section": firstRecommendationsGameBlock.value.groupType,
                title: firstRecommendationsGameBlock.value.title,
                rows: firstRecommendationsGameBlock.value.rows,
                games: firstRecommendationsGameBlock.value.games
              }, null, 8, ["data-interface-section", "title", "rows", "games"]))
            : _createCommentVNode("", true),
          _createVNode(LazyHydration, {
            hydrate: hydrateOnIdle()
          }, {
            default: _withCtx(() => [
              (_unref(game) && _unref(gameCatInfo))
                ? (_openBlock(), _createBlock(GameDetails, {
                    key: 0,
                    game: _unref(game),
                    gameCatInfo: _unref(gameCatInfo)
                  }, null, 8, ["game", "gameCatInfo"]))
                : _createCommentVNode("", true),
              (showRelatedGames.value && hasGameSeries.value)
                ? (_openBlock(), _createBlock(GameCardCarousel, {
                    key: 1,
                    "data-interface-section": "related",
                    title: showRecommendationsTitle('related'),
                    games: relatedGames.value
                  }, null, 8, ["title", "games"]))
                : _createCommentVNode("", true),
              (showSpotlightGames.value)
                ? (_openBlock(), _createBlock(GameCardCarousel, {
                    key: 2,
                    "data-interface-section": "top_playgama",
                    title: showRecommendationsTitle('top_playgama'),
                    games: spotlightGames.value,
                    big: ""
                  }, null, 8, ["title", "games"]))
                : _createCommentVNode("", true),
              (showRecommendedGames.value)
                ? (_openBlock(), _createBlock(GameCardCarousel, {
                    key: 3,
                    "data-interface-section": "recommended",
                    title: showRecommendationsTitle('recommended'),
                    games: recommendedGames.value
                  }, null, 8, ["title", "games"]))
                : _createCommentVNode("", true),
              (showRandomGames.value)
                ? (_openBlock(), _createBlock(GameCardCarousel, {
                    key: 4,
                    "data-interface-section": "random",
                    title: showRecommendationsTitle('random'),
                    games: randomGames.value
                  }, null, 8, ["title", "games"]))
                : _createCommentVNode("", true),
              _createVNode(PromoCategories),
              _createVNode(TheEnd),
              _createVNode(Breadcrumbs, { items: breadcrumps.value }, null, 8, ["items"])
            ]),
            _: 1
          }, 8, ["hydrate"]),
          _createVNode(LazyHydration, {
            hydrate: hydrateOnIdle()
          }, {
            default: _withCtx(() => [
              _createVNode(ModalReportBug, {
                isOpen: isReportModalOpen.value,
                page: _unref(route).fullPath,
                onCloseReportModal: _cache[1] || (_cache[1] = ($event: any) => (_unref(setIsReportBugModalOpen)(false)))
              }, null, 8, ["isOpen", "page"])
            ]),
            _: 1
          }, 8, ["hydrate"])
        ], 2)
      ]),
      _: 1
    })
  ], 64))
}
}

})